/** Provides drag-to-resize state for a fixed right-side panel. */
import React, { createContext, useContext } from "react";

import { useResizableWidth } from "src/base-components/useResizableWidth";

const MIN_WIDTH = 420;
const MAX_WIDTH = 800;

type ResizableSidepaneContextType = {
  width: number;
  isResizing: boolean;
  handleResizeStart: () => void;
};

const ResizableSidepaneContext = createContext<
  ResizableSidepaneContextType | undefined
>(undefined);

export const useResizableSidepane = (): ResizableSidepaneContextType => {
  const context = useContext(ResizableSidepaneContext);
  if (!context) {
    throw new Error(
      "useResizableSidepane must be used within a ResizableSidepaneProvider",
    );
  }
  return context;
};

export const ResizeHandleButton: React.FC<{
  onResizeStart: () => void;
}> = ({ onResizeStart }) => (
  <button
    className="absolute bottom-0 left-0 h-full w-1 cursor-col-resize"
    onMouseDown={onResizeStart}
  />
);

/**
 * Drag handle rendered on the left edge of the sidepane.
 * Must be used inside a ResizableSidepaneProvider.
 */
export const ResizeHandle: React.FC = () => {
  const { handleResizeStart } = useResizableSidepane();

  return <ResizeHandleButton onResizeStart={handleResizeStart} />;
};

type ResizableSidepaneProviderProps = {
  children: React.ReactNode;
  defaultWidth?: number;
  maxWidth?: number;
  storageKey?: string;
};

export const ResizableSidepaneProvider: React.FC<
  ResizableSidepaneProviderProps
> = ({ children, defaultWidth, maxWidth, storageKey }) => {
  const { width, isResizing, handleResizeStart } = useResizableWidth({
    side: "right",
    storageKey: storageKey ?? "resizable-sidepane-width",
    minWidth: MIN_WIDTH,
    maxWidth: maxWidth ?? MAX_WIDTH,
    defaultWidth,
  });

  return (
    <ResizableSidepaneContext value={{ width, isResizing, handleResizeStart }}>
      {children}
    </ResizableSidepaneContext>
  );
};
